import { DropdownSelectionInput } from '@adornis/formfield/fields/dropdown-selection-input.js';
import { TextareaInput } from '@adornis/formfield/fields/textarea-input.js';
import { useForm } from '@adornis/forms/use-form.js';
import { stateful, useEffect, useState } from '@adornis/functional-lit/stateful.js';
import { html, nothing } from 'lit';
import type { UserNotification } from '../../features/notifications/db/UserNotification.js';

/**
 * The answer to one notification: pick an offered option, write in your own words, or both.
 *
 * An option that carries a patch is applied deterministically on the server — no model is
 * asked. Free text is recorded on the request until the interpretation step exists.
 */
interface AnswerDraft {
  /** Empty string means "no option picked" — keeps the field type plain `string`. */
  optionKey: string;
  freeText: string;
}

const emptyDraft = (): AnswerDraft => ({ optionKey: '', freeText: '' });

interface NotificationAnswerFormProps {
  notification: UserNotification;
  busy: boolean;
  onSubmit: (optionKey: string | null, freeText: string) => void;
}

export const NotificationAnswerForm = stateful((props: NotificationAnswerFormProps) => {
  const { notification, busy } = props;
  const [draft, setDraft] = useState<AnswerDraft>(emptyDraft());

  // The component instance survives a switch to another notification, so the draft has to be
  // cleared explicitly — otherwise one request's answer would be offered for the next.
  useEffect(() => {
    setDraft(emptyDraft());
  }, [notification._id]);

  const { document: answer, field } = useForm(draft, { validator: undefined }, [draft]);
  const options = notification.options ?? [];
  const optionLabel = (key: string) => options.find(option => option.key === key)?.label ?? key;

  const submit = () => {
    props.onSubmit(answer.optionKey || null, answer.freeText.trim());
  };

  return html`
    <div class="rounded-2xl bg-base-100 border border-base-200 shadow-sm mt-4">
      <div class="px-5 py-3 border-b border-base-200">
        <h2 class="text-sm font-semibold uppercase tracking-wide text-base-content/50">Ihre Antwort</h2>
      </div>
      <div class="p-5 flex flex-col gap-4">
        ${options.length
          ? html`
              <div>
                <label class="label-text font-medium mb-1 block">Antwort wählen</label>
                ${DropdownSelectionInput<string>({
                  ...field('optionKey'),
                  selectables: options.map(option => option.key),
                  renderString: optionLabel,
                  placeholder: 'Bitte wählen …',
                  clearable: true,
                  disabled: busy,
                })}
              </div>
            `
          : nothing}
        ${notification.allowsFreeText
          ? html`
              <div>
                <label class="label-text font-medium mb-1 block">
                  ${options.length ? 'Oder in eigenen Worten' : 'Antwort in eigenen Worten'}
                </label>
                ${TextareaInput({
                  ...field('freeText'),
                  rows: 3,
                  placeholder: 'Was fehlt hier?',
                  disabled: busy,
                })}
                <p class="text-xs text-base-content/50 mt-1">
                  Freitext wird zunächst als Notiz an der Anfrage festgehalten — die automatische Auswertung kommt
                  später.
                </p>
              </div>
            `
          : nothing}
        <div>
          <button class="btn btn-primary btn-sm gap-2" ?disabled=${busy} @click=${submit}>
            <x-icon class="text-base">send</x-icon>Antwort senden
          </button>
        </div>
      </div>
    </div>
  `;
});
